
import { supabase } from "@/integrations/supabase/client";
import { AuthResult } from "./types";
import { refreshSessionViaProxy } from "@/utils/authProxy";

/**
 * Refreshes the user's authentication session
 * Attempts to use the auth-proxy first, falls back to direct refresh
 */
export const refreshSession = async (): Promise<AuthResult> => {
  try {
    console.log("Attempting to refresh session");
    const { data: sessionData } = await supabase.auth.getSession();
    
    if (!sessionData.session) {
      console.log("No session to refresh");
      return { data: null, error: new Error("No active session to refresh") };
    }
    
    const refreshToken = sessionData.session.refresh_token;
    
    // First try refresh via proxy
    console.log("Attempting to refresh via proxy");
    const proxyResult = await refreshSessionViaProxy(refreshToken);
    
    if (proxyResult.data && !proxyResult.error) {
      // If proxy successful, update the session
      console.log("Proxy refresh successful");
      
      const { error: sessionError } = await supabase.auth.setSession({
        access_token: proxyResult.data.access_token,
        refresh_token: proxyResult.data.refresh_token,
      });
      
      if (sessionError) {
        console.error("Error setting refreshed session:", sessionError);
        throw sessionError;
      }
      
      return { 
        data: { 
          session: {
            access_token: proxyResult.data.access_token,
            refresh_token: proxyResult.data.refresh_token,
            user: proxyResult.data.user
          } 
        }, 
        error: null 
      };
    }
    
    // If proxy fails, try direct refresh
    console.log("Falling back to direct refresh");
    
    const { data, error } = await supabase.auth.refreshSession({
      refresh_token: refreshToken,
    });
    
    if (error) throw error;
    return { data, error: null };
    
  } catch (error: any) {
    console.error("Error refreshing session:", error.message);
    return { data: null, error };
  }
};

/**
 * Gets the current session
 */
export const getSession = async (): Promise<AuthResult> => {
  try {
    const { data, error } = await supabase.auth.getSession();
    if (error) throw error;
    return { data, error: null };
  } catch (error: any) {
    console.error("Error getting session:", error.message);
    return { data: null, error };
  }
};
